import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, Settings, Plus, Server, RefreshCw, Trash2, Edit2,
  Search, Filter, Download, AlertTriangle, Terminal, Cpu,
  CheckCircle2, XCircle, Eye, EyeOff, Check, Activity, AlertCircle, Users
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ToggleRow = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between cursor-pointer group gap-4">
    <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{label}</span>
    <div className="relative inline-block w-11 flex-shrink-0">
      <input type="checkbox" className="peer sr-only" checked={checked} onChange={e => onChange(e.target.checked)}/>
      <div className={`w-11 h-6 rounded-full transition-colors border ${checked ? "bg-[#0EA5E9] border-[#0EA5E9]" : "bg-[#1a1a1a] border-white/10"}`}>
        <div className={`absolute top-[3px] left-[3px] bg-white rounded-full h-[18px] w-[18px] transition-transform duration-200 shadow-sm ${checked ? "translate-x-5" : "translate-x-0"}`}/>
      </div>
    </div>
  </label>
);

export default function HomebridgeAdminPanel() {
  const [instances, setInstances] = useState([]);
  const [cmdLog, setCmdLog] = useState([]);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({
    enabled: true, require_token: true, allow_voice: true,
    logging: true, timeout: 10, retries: 3, sync_interval: 30
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [instR, statsR, settingsR] = await Promise.all([
        axios.get(`${API}/homebridge/admin/instances`),
        axios.get(`${API}/homebridge/admin/stats`),
        axios.get(`${API}/homebridge/admin/settings`),
      ]);
      setInstances(instR.data.instances || []);
      setStats(statsR.data || {});
      setSettings(statsR.data.settings || settingsR.data || settings);
    } catch (e) {
      // Fresh install — no data yet
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/homebridge/admin/settings`, settings);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSavingSettings(false);
    }
  };

  const deleteInstance = async (id) => {
    if (!window.confirm("¿Eliminar esta instancia?")) return;
    try {
      await axios.delete(`${API}/homebridge/admin/instances/${id}`);
      toast.success("Instancia eliminada");
      loadAll();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const filtered = instances.filter(i => i.name?.toLowerCase().includes(search.toLowerCase()) || i.url?.includes(search));

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Instancias", val: stats.total_instances ?? "–", color: "text-[#0EA5E9]" },
          { label: "Comandos", val: stats.total_commands ?? "–", color: "text-white" },
          { label: "Errores", val: stats.error_commands ?? "–", color: "text-[#E53935]" },
        ].map((s, i) => (
          <div key={i} className="glass-panel p-4">
            <p className="text-gray-500 text-xs mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Global Settings */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/5">
          <Settings className="w-4 h-4 text-gray-400"/>
          <h3 className="text-base font-semibold text-gray-100">Configuración Global</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-5">
          <div className="space-y-4">
            <ToggleRow label="Activar integración Homebridge" checked={settings.enabled} onChange={v => setSettings({...settings, enabled: v})}/>
            <ToggleRow label="Requerir autenticación por token" checked={settings.require_token} onChange={v => setSettings({...settings, require_token: v})}/>
            <ToggleRow label="Permitir control de voz" checked={settings.allow_voice} onChange={v => setSettings({...settings, allow_voice: v})}/>
            <ToggleRow label="Logging de comandos" checked={settings.logging} onChange={v => setSettings({...settings, logging: v})}/>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Timeout (seg)</label>
              <input type="number" value={settings.timeout} onChange={e => setSettings({...settings, timeout: parseInt(e.target.value)})}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-[#0EA5E9]"/>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Reintentos</label>
              <input type="number" value={settings.retries} onChange={e => setSettings({...settings, retries: parseInt(e.target.value)})}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-[#0EA5E9]"/>
            </div>
          </div>
          <div className="flex flex-col justify-end">
            <button onClick={saveSettings} disabled={savingSettings}
              className="bg-[#E53935] hover:bg-[#C62828] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50">
              {savingSettings ? "Guardando..." : "Guardar configuración global"}
            </button>
          </div>
        </div>
      </div>

      {/* Instances Table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-100 flex items-center gap-2">
            <Server className="w-5 h-5 text-[#0EA5E9]"/> Instancias de Usuarios
          </h3>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2"/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..." className="bg-[#111] border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-[#0EA5E9] w-52"/>
          </div>
        </div>

        <div className="glass-panel rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-gray-500"/></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No hay instancias configuradas aún.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-[#0a0a0a]/50 text-gray-500 border-b border-white/5 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-3.5">Nombre</th>
                    <th className="px-5 py-3.5">URL</th>
                    <th className="px-5 py-3.5">Usuario</th>
                    <th className="px-5 py-3.5">Creado</th>
                    <th className="px-5 py-3.5 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filtered.map((inst, i) => (
                    <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-3.5 font-medium text-gray-200">{inst.name}</td>
                      <td className="px-5 py-3.5 text-[#0EA5E9] font-mono text-xs truncate max-w-[200px]">{inst.url}</td>
                      <td className="px-5 py-3.5 text-gray-400 font-mono text-xs">{inst.user_id?.slice(0, 8)}…</td>
                      <td className="px-5 py-3.5 text-gray-500 text-xs">{inst.created_at ? new Date(inst.created_at).toLocaleDateString("es-MX") : "—"}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => deleteInstance(inst.id)}
                          className="p-1.5 rounded text-gray-500 hover:text-[#E53935] hover:bg-[#E53935]/10 transition-colors">
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-[#151111] border border-[#E53935]/30 rounded-xl p-6">
        <h3 className="text-base font-bold text-[#E53935] flex items-center gap-2 mb-5">
          <AlertTriangle className="w-5 h-5"/> Zona de Peligro
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-200">Limpiar Log de Comandos</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Elimina todos los registros del historial de comandos de todos los usuarios.</p>
            <button className="w-full px-4 py-2 rounded-lg border border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-sm font-medium transition-colors"
              onClick={async () => { toast.info("Función próximamente disponible"); }}>
              Limpiar Historial
            </button>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-200">Deshabilitar Todas las Instancias</h4>
            <p className="text-xs text-gray-500 leading-relaxed">Desactiva la integración Homebridge a nivel global para todos los usuarios.</p>
            <button className="w-full px-4 py-2 rounded-lg bg-[#E53935] text-white hover:bg-[#D32F2F] text-sm font-medium transition-colors"
              onClick={() => { setSettings({...settings, enabled: false}); toast.warning("Integración deshabilitada — recuerda guardar."); }}>
              Deshabilitar Integración
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
